import { useEffect, useRef } from 'react';

interface Star {
  x: number;
  y: number;
  size: number;
  opacity: number;
  baseOpacity: number;
  twinkleSpeed: number;
  twinklePhase: number;
  color: string;
  flare: boolean;
}

const StarsBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let stars: Star[] = [];
    let isPaused = document.hidden;
    let scrollTimeout: number;
    const isMobile = window.innerWidth < 768;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const onVisibilityChange = () => { isPaused = document.hidden; };
    const onScroll = () => {
      isPaused = true;
      clearTimeout(scrollTimeout);
      scrollTimeout = window.setTimeout(() => { isPaused = false; }, 120);
    };

    const colors = [
      '#ffffff',
      '#fce7ff', // soft pink white
      '#e9d5ff', // lavender
      '#ff7ad9', // hot pink
      '#c084fc', // violet
    ];

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      init();
    };

    const init = () => {
      stars = [];
      const w = window.innerWidth;
      const h = window.innerHeight;
      const density = isMobile ? 2800 : 1700;
      const max = isMobile ? 700 : 1100;
      const num = Math.min(Math.floor((w * h) / density), max);

      for (let i = 0; i < num; i++) {
        const r = Math.random();
        let size: number, baseOpacity: number;
        if (r < 0.78) {
          size = 0.6 + Math.random() * 0.5;
          baseOpacity = 0.35 + Math.random() * 0.4;
        } else if (r < 0.97) {
          size = 1.1 + Math.random() * 0.8;
          baseOpacity = 0.55 + Math.random() * 0.35;
        } else {
          size = 1.8 + Math.random() * 1.4;
          baseOpacity = 0.8 + Math.random() * 0.2;
        }

        stars.push({
          x: Math.random() * w,
          y: Math.random() * h,
          size,
          baseOpacity,
          opacity: baseOpacity,
          twinkleSpeed: 0.4 + Math.random() * 1.2,
          twinklePhase: Math.random() * Math.PI * 2,
          color: colors[Math.floor(Math.random() * colors.length)],
          // ~3% get a cross flare, suppressed on mobile
          flare: !isMobile && size > 1.6 && Math.random() < 0.35,
        });
      }
    };

    const drawStar = (s: Star) => {
      ctx.globalAlpha = s.opacity;

      if (s.size < 1) {
        // Crisp 1px pixels — astrophotography feel
        ctx.fillStyle = s.color;
        ctx.fillRect(Math.floor(s.x), Math.floor(s.y), 1, 1);
        return;
      }

      if (s.size < 1.7) {
        ctx.fillStyle = s.color;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.size * 0.45, 0, Math.PI * 2);
        ctx.fill();
        return;
      }

      // Large star — controlled glow
      ctx.shadowBlur = 3;
      ctx.shadowColor = s.color;
      ctx.fillStyle = s.color;
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.size * 0.55, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;

      if (s.flare) {
        ctx.globalAlpha = s.opacity * 0.55;
        ctx.strokeStyle = s.color;
        ctx.lineWidth = 0.6;
        const len = s.size * 5;
        ctx.beginPath();
        ctx.moveTo(s.x - len, s.y);
        ctx.lineTo(s.x + len, s.y);
        ctx.moveTo(s.x, s.y - len);
        ctx.lineTo(s.x, s.y + len);
        ctx.stroke();
      }
    };

    const targetFPS = reducedMotion ? 1 : 24;
    const frameInterval = 1000 / targetFPS;
    let lastFrame = 0;

    const animate = (t: number) => {
      animationFrameId = requestAnimationFrame(animate);
      if (isPaused) { lastFrame = t; return; }
      const elapsed = t - lastFrame;
      if (elapsed < frameInterval) return;
      lastFrame = t - (elapsed % frameInterval);

      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      const ts = t * 0.001;

      for (const s of stars) {
        s.opacity = s.baseOpacity + Math.sin(ts * s.twinkleSpeed + s.twinklePhase) * 0.18;
        if (s.opacity < 0.05) s.opacity = 0.05;
        drawStar(s);
      }
      ctx.globalAlpha = 1;
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', resize);
    resize();
    animate(0);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('scroll', onScroll as EventListener);
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      role="presentation"
      className="absolute top-0 left-0 w-full h-full pointer-events-none"
      style={{ zIndex: 4, willChange: 'transform', transform: 'translateZ(0)' }}
    />
  );
};

export default StarsBackground;
